import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormCard, FormGrid } from "../FormCard";
import {
  CreatableSelectRow,
  InputRow,
  SwitchRow,
  SelectRow,
} from "../Rows";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { buildParkingSpacesSummary } from "./locationCardSummaries";
import { withTranslatedLabels } from "../../i18n/options";

const createEmptyParkingSpace = () => ({
  parkingPublicId: null,
  physicalReference: "",
  vehicleTypes: [],
  restrictedToType: false,
  reservationRequired: false,
  maxVehicleWeight: "",
  maxVehicleHeight: "",
  maxVehicleLength: "",
  maxVehicleWidth: "",
  parkingSpaceLength: "",
  parkingSpaceWidth: "",
  dangerousGoodsAllowed: false,
  direction: "",
  driveThrough: false,
  timeLimit: "",
  roofed: false,
  lighting: false,
  refrigerationOutlet: false,
  standards: [],
  apdsReference: "",
});

const vehicleTypeOptions = [
  { value: "MOTORCYCLE" },
  { value: "PERSONAL_VEHICLE" },
  { value: "PERSONAL_VEHICLE_WITH_TRAILER" },
  { value: "VAN" },
  { value: "SEMI_TRACTOR" },
  { value: "RIGID" },
  { value: "TRUCK_WITH_TRAILER" },
  { value: "BUS" },
  { value: "DISABLED" },
];

const standardsOptions = [
  {
    label: "PAS 1899",
    value: "PAS 1899",
  },
  {
    label: "ISO 21542",
    value: "ISO 21542",
  },
  {
    label: "EN 17210",
    value: "EN 17210",
  },
];

const directionOptions = [
  { value: "PARALLEL" },
  { value: "PERPENDICULAR" },
  { value: "ANGLE" },
];

function ParkingSpace({
  data = [],
  onChange = () => {},
  invalidVehicleTypesIndices = [],
  onClearVehicleTypesInvalid,
  invalidPhysicalReferenceIndices = [],
  onClearPhysicalReferenceInvalid,
}) {
  const { t } = useTranslation();
  const [parkingSpaces, setParkingSpaces] = useState(data);
  const lastEmittedRef = useRef(data);

  useEffect(() => {
    if (data === lastEmittedRef.current) {
      return;
    }

    lastEmittedRef.current = data;
    setParkingSpaces(data);
  }, [data]);

  const commitParkingSpaces = (next) => {
    lastEmittedRef.current = next;
    setParkingSpaces(next);
    onChange(next);
  };

  const updateSpace = (index, key, value) => {
    if (key === "vehicleTypes" && typeof onClearVehicleTypesInvalid === "function") {
      onClearVehicleTypesInvalid(index);
    }
    if (
      key === "physicalReference" &&
      typeof onClearPhysicalReferenceInvalid === "function"
    ) {
      onClearPhysicalReferenceInvalid(index);
    }

    commitParkingSpaces(
      parkingSpaces.map((space, currentIndex) =>
        currentIndex === index ? { ...space, [key]: value } : space
      )
    );
  };

  const addSpace = () => {
    commitParkingSpaces([...parkingSpaces, createEmptyParkingSpace()]);
  };

  const removeSpace = (index) => {
    commitParkingSpaces(
      parkingSpaces.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  const translatedVehicleTypes = withTranslatedLabels(
    vehicleTypeOptions,
    t,
    "site.location.vehicleType"
  );
  const translatedDirections = withTranslatedLabels(
    directionOptions,
    t,
    "site.location.directionValue"
  );

  return (
    <FormCard
      title={t("site.location.parkingTitle")}
      description={t("site.location.parkingDescription")}
      summary={buildParkingSpacesSummary(parkingSpaces, t)}
      collapsible
      defaultOpen={false}
    >
      <div className="flex flex-col gap-4">
        {parkingSpaces.map((space, index) => {
          const itemKey = space.parkingPublicId || `parking-space-${index}`;

          return (
            <FormCard
              key={itemKey}
              title={
                <span className="inline-flex items-center gap-3">
                  <span>
                    {space.physicalReference?.trim() ||
                      t("site.location.parkingSpaceN", { n: index + 1 })}
                  </span>
                  <button
                    type="button"
                    className="text-error-500 transition hover:text-error-600"
                    onClick={() => removeSpace(index)}
                    title={t("site.location.removeParkingSpace")}
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-4" />
                  </button>
                </span>
              }
              description={
                space.parkingPublicId ? t("site.location.updateParkingSpace") : t("site.location.newParkingSpace")
              }
              collapsible
              defaultOpen={true}
              titleClassName="pr-12"
            >
              <FormGrid>
                <InputRow
                  label={t("site.location.physicalRef")}
                  tooltip={t("site.location.tooltip.physicalReference")}
                  value={space.physicalReference}
                  invalid={invalidPhysicalReferenceIndices.includes(index)}
                  onChange={(value) =>
                    updateSpace(index, "physicalReference", value)
                  }
                  maxLength={12}
                />

                <SelectRow
                  label={t("site.location.vehicleTypes")}
                  tooltip={t("site.location.tooltip.vehicleTypes")}
                  value={space.vehicleTypes || []}
                  options={translatedVehicleTypes}
                  isMulti
                  invalid={invalidVehicleTypesIndices.includes(index)}
                  invalidMessage={
                    invalidVehicleTypesIndices.includes(index)
                      ? t("site.location.selectVehicleType")
                      : undefined
                  }
                  onChange={(value) => updateSpace(index, "vehicleTypes", value)}
                />

                <SelectRow
                  label={t("site.location.direction")}
                  tooltip={t("site.location.tooltip.direction")}
                  value={String(space.direction || "").toUpperCase()}
                  options={translatedDirections}
                  onChange={(value) => updateSpace(index, "direction", value)}
                  isClearable
                />

                <CreatableSelectRow
                  label={t("site.location.standards")}
                  tooltip={t("site.location.tooltip.standards")}
                  value={space.standards || []}
                  options={standardsOptions}
                  isMulti
                  onChange={(value) => updateSpace(index, "standards", value)}
                />

                <InputRow
                  label={t("site.location.maxWeightKg")}
                  tooltip={t("site.location.tooltip.maxVehicleWeight")}
                  value={space.maxVehicleWeight}
                  isNumber
                  onChange={(value) =>
                    updateSpace(index, "maxVehicleWeight", value)
                  }
                />

                <InputRow
                  label={t("site.location.maxHeightCm")}
                  tooltip={t("site.location.tooltip.maxVehicleHeight")}
                  value={space.maxVehicleHeight}
                  isNumber
                  onChange={(value) =>
                    updateSpace(index, "maxVehicleHeight", value)
                  }
                />

                <InputRow
                  label={t("site.location.maxLengthCm")}
                  tooltip={t("site.location.tooltip.maxVehicleLength")}
                  value={space.maxVehicleLength}
                  isNumber
                  onChange={(value) =>
                    updateSpace(index, "maxVehicleLength", value)
                  }
                />

                <InputRow
                  label={t("site.location.maxWidthCm")}
                  tooltip={t("site.location.tooltip.maxVehicleWidth")}
                  value={space.maxVehicleWidth}
                  isNumber
                  onChange={(value) =>
                    updateSpace(index, "maxVehicleWidth", value)
                  }
                />

                <InputRow
                  label={t("site.location.spaceLengthCm")}
                  tooltip={t("site.location.tooltip.parkingSpaceLength")}
                  value={space.parkingSpaceLength}
                  isNumber
                  onChange={(value) =>
                    updateSpace(index, "parkingSpaceLength", value)
                  }
                />

                <InputRow
                  label={t("site.location.spaceWidthCm")}
                  tooltip={t("site.location.tooltip.parkingSpaceWidth")}
                  value={space.parkingSpaceWidth}
                  isNumber
                  onChange={(value) =>
                    updateSpace(index, "parkingSpaceWidth", value)
                  }
                />

                <InputRow
                  label={t("site.location.timeLimitMin")}
                  tooltip={t("site.location.tooltip.timeLimit")}
                  value={space.timeLimit}
                  isNumber
                  onChange={(value) => updateSpace(index, "timeLimit", value)}
                />

                <InputRow
                  label={t("site.location.apdsRef")}
                  tooltip={t("site.location.tooltip.apdsReference")}
                  value={space.apdsReference}
                  onChange={(value) =>
                    updateSpace(index, "apdsReference", value)
                  }
                />

                <SwitchRow
                  label={t("site.location.restrictedType")}
                  tooltip={t("site.location.tooltip.restrictedToType")}
                  checked={space.restrictedToType}
                  onChange={(value) =>
                    updateSpace(index, "restrictedToType", value)
                  }
                />

                <SwitchRow
                  label={t("site.location.reservationRequired")}
                  tooltip={t("site.location.tooltip.reservationRequired")}
                  checked={space.reservationRequired}
                  onChange={(value) =>
                    updateSpace(index, "reservationRequired", value)
                  }
                />

                <SwitchRow
                  label={t("site.location.dangerousGoods")}
                  tooltip={t("site.location.tooltip.dangerousGoodsAllowed")}
                  checked={space.dangerousGoodsAllowed}
                  onChange={(value) =>
                    updateSpace(index, "dangerousGoodsAllowed", value)
                  }
                />

                <SwitchRow
                  label={t("site.location.driveThrough")}
                  tooltip={t("site.location.tooltip.driveThrough")}
                  checked={space.driveThrough}
                  onChange={(value) =>
                    updateSpace(index, "driveThrough", value)
                  }
                />

                <SwitchRow
                  label={t("site.location.roofed")}
                  tooltip={t("site.location.tooltip.roofed")}
                  checked={space.roofed}
                  onChange={(value) => updateSpace(index, "roofed", value)}
                />

                <SwitchRow
                  label={t("site.location.lighting")}
                  tooltip={t("site.location.tooltip.lighting")}
                  checked={space.lighting}
                  onChange={(value) => updateSpace(index, "lighting", value)}
                />

                <SwitchRow
                  label={t("site.location.refrigeration")}
                  tooltip={t("site.location.tooltip.refrigerationOutlet")}
                  checked={space.refrigerationOutlet}
                  onChange={(value) =>
                    updateSpace(index, "refrigerationOutlet", value)
                  }
                />
              </FormGrid>
            </FormCard>
          );
        })}

        <div>
          <button type="button" className="btn btn-primary" onClick={addSpace}>
            {t("site.location.addParkingSpace")}
          </button>
        </div>
      </div>
    </FormCard>
  );
}

export default ParkingSpace;
